/**
 * AboutPage — the marketing/explainer content that used to sit below
 * the landing hero now lives here. The landing itself is now hero +
 * sign-in only; anyone who wants the deeper story lands here.
 *
 * Shares the same `LandingHeader` / `LandingFooter` as `/`, so the
 * chrome (colour, brand mark, sign-in modal) stays consistent.
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCountry } from "@/lib/country";
import SeoHead from "@/components/SeoHead";
import {
    LandingHeader,
    LandingFooter,
    LandingTabs,
    LandingSearchSection,
    MeetLane,
    CareProviderInterestStrip,
    OrganizationsBand,
    ImpactNumbers,
    FinalCTA,
    FourFeatureCards,
    JourneyOverlay,
    RoadmapMilestones,
    QuickSignInModal,
} from "@/pages/Landing";

/* "Where Memory Lane fits" — positions the platform as the continuity
 * layer BETWEEN the excellent support that already exists (helplines,
 * education nonprofits, in-person day programs), never against it. */
function HowWeFitSection() {
    const PILLARS = [
        {
            label: "Education & helplines",
            example: "like the Alzheimer's Association's 24/7 Helpline",
            great: "Trusted answers, support groups, and a human voice at any hour — for every family in America.",
            gap: "Each call starts from zero. No one there knows your mom's story.",
        },
        {
            label: "In-person programs",
            example: "like dementia day centers and memory cafés",
            great: "Real respite, engagement, and community you can walk into — the kind of care software can't replace.",
            gap: "The knowledge lives on-site. It doesn't follow you to the neurologist on Thursday.",
        },
        {
            label: "Memory Lane",
            example: "the continuity layer between them",
            great: "One living record of your family's journey — behaviors, questions, wellbeing, visit prep — that travels to every appointment and every provider.",
            gap: null,
            highlight: true,
        },
    ];
    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="about-how-we-fit">
            <div className="text-center max-w-2xl mx-auto">
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-dark">Where Memory Lane fits</p>
                <h2 className="mt-2 font-heading font-semibold text-2xl md:text-3xl text-[#12776F]">
                    Great support already exists. It just doesn&apos;t remember you.
                </h2>
                <p className="mt-3 text-sm md:text-base text-[#4C5551] leading-relaxed">
                    Helplines, education nonprofits, and day programs are each a moment of help.
                    Memory Lane is the memory between the moments — and it makes every one of them work better.
                </p>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                {PILLARS.map((p) => (
                    <div key={p.label}
                        data-testid={`about-fit-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`rounded-3xl p-6 border ${p.highlight
                            ? "bg-teal-dark text-white border-teal-dark shadow-[0_14px_40px_rgba(15,76,92,0.22)]"
                            : "bg-white border-[#E7E0CE]"}`}>
                        <p className={`text-xs uppercase tracking-wider font-semibold ${p.highlight ? "text-teal-soft" : "text-teal-dark"}`}>{p.label}</p>
                        <p className={`mt-0.5 text-xs ${p.highlight ? "text-white/70" : "text-[#9CA6A1]"}`}>{p.example}</p>
                        <p className={`mt-3 text-sm leading-relaxed ${p.highlight ? "text-white" : "text-[#2A3330]"}`}>{p.great}</p>
                        {p.gap && <p className="mt-3 text-sm leading-relaxed text-[#8A5A2B] bg-cream-light rounded-xl px-3 py-2">{p.gap}</p>}
                        {p.highlight && (
                            <p className="mt-3 text-sm leading-relaxed text-teal-soft">
                                We route families TO helplines and local programs at the right stage — arriving prepared, with their story in hand.
                            </p>
                        )}
                    </div>
                ))}
            </div>
            <p className="mt-5 text-center text-xs text-[#6E7A74]">
                Run a helpline, day program, or memory clinic? <Link to="/partners" className="text-teal-dark underline">We&apos;d love to send families your way</Link>.
            </p>
        </section>
    );
}

export default function AboutPage() {
    const { t } = useCountry();
    const [signInOpen, setSignInOpen] = useState(false);

    return (
        <div className="min-h-screen bg-cream" id="top">
            <SeoHead
                title="About Memory Lane — A continuity platform for dementia care"
                description="Memory Lane is a caregiver-first navigation platform built by a nurse practitioner for families noticing memory changes, navigating a new diagnosis, or living with dementia. See how it works, meet Lane, and read the story behind it."
                canonicalPath="/about"
            />

            <LandingHeader onOpenSignIn={() => setSignInOpen(true)} t={t} />

            {/* Small hero for the About page — reintroduces the page and
                sets tone without competing with the marketing sections
                below. */}
            <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 md:pt-14 pb-4">
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-dark">
                    About Memory Lane
                </p>
                <h1 className="mt-2 font-heading font-semibold tracking-tight leading-[1.1] text-3xl md:text-5xl text-[#12776F]">
                    A continuity platform for the long road through dementia.
                </h1>
                <p className="mt-4 text-base md:text-lg text-[#4C5551] leading-relaxed max-w-2xl">
                    Built by a nurse practitioner who kept meeting families who&apos;d already been through too much
                    before anyone gave them a map. Memory Lane is the map — personalized, non-linear,
                    and there whenever the road bends again.
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                    <Link to="/beta" className="ml-btn-primary text-sm !py-2 !px-5" data-testid="about-cta-try">
                        Try Memory Lane
                    </Link>
                    <button
                        type="button"
                        onClick={() => setSignInOpen(true)}
                        className="ml-btn-secondary text-sm !py-2 !px-5"
                        data-testid="about-cta-signin"
                    >
                        Sign in
                    </button>
                </div>
            </section>

            {/* Marketing sections in the order caregivers naturally read them. */}
            <FourFeatureCards />

            <HowWeFitSection />

            <JourneyOverlay />

            {/* Compact roadmap strip — kept as a visual continuity anchor. */}
            <section
                aria-label="The caregiver journey roadmap"
                data-testid="about-roadmap-strip"
                className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8"
            >
                <div className="ml-card p-5 md:p-6 bg-white">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark text-center mb-1">
                        Your individualized roadmap
                    </p>
                    <p className="text-sm md:text-base text-[#4C5551] text-center mb-5 max-w-xl mx-auto">
                        Meets you where you are on the journey.
                    </p>
                    <div className="relative">
                        <svg viewBox="0 0 1000 60" className="w-full h-12 md:h-16" preserveAspectRatio="none" aria-hidden="true">
                            <path d="M 20 40 Q 200 5, 250 30 T 500 30 T 750 30 T 980 25"
                                stroke="#1D8A85" strokeWidth="3" strokeDasharray="8 8" strokeLinecap="round" fill="none" opacity="0.6" />
                        </svg>
                        <RoadmapMilestones />
                    </div>
                </div>
            </section>

            <LandingSearchSection />
            <MeetLane />
            <CareProviderInterestStrip />
            <LandingTabs />
            <OrganizationsBand />
            <ImpactNumbers />
            <FinalCTA />

            <LandingFooter t={t} />

            <QuickSignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
        </div>
    );
}
